// @ts-strict-ignore
import { getProperty, setProperty } from 'dot-prop';
import cloneDeep from 'lodash/cloneDeep';
import { getNumericPathIndices } from 'flow/service/actions';
import { ActionType, ExpressionUpdateType } from '../constants';
import type {
  Action,
  AttributeComponent,
  DataRoute,
  Expression,
  ExpressionTerm,
  SchemaInstance,
  WorkflowProps,
} from '../types';
import { getIsExpressionOperatorString } from '../utils';
import type { Attribute } from '~/flow/types';
import { getParentPathAndExpressionItem } from '../../redux/helpers';
import { transformMLAAttributeComponents } from '../transformation/thresholds';
import { isAggregationServiceAction } from '../../../service/services';
import { getDefaultAttribute, resolveAggregationComponents } from './helpers';
import { resolveServiceOrUniversalAttribute } from 'flow/service/thresholds';

export function findDataRouteIdAndRemove({
  dataRouteId,
  originalExpression,
  allowEmptyParentGroup,
}: {
  dataRouteId: string;
  originalExpression: Expression;
  allowEmptyParentGroup?: boolean;
}) {
  return originalExpression.reduce((
    result: Expression,
    expressionObject: ExpressionTerm | ExpressionTerm[],
  ) => {
    if (typeof expressionObject !== 'string'
      && typeof expressionObject !== 'number'
      && 'data_route_id' in expressionObject && expressionObject.data_route_id !== dataRouteId) {
      result.push(expressionObject);
    }

    if (typeof expressionObject === 'number') {
      result.push(expressionObject);
    }

    if (typeof expressionObject === 'string') {
      if (getIsExpressionOperatorString(expressionObject)) {
        if (result.length > 0 && typeof result[result.length - 1] !== 'string') {
          result.push(expressionObject);
        }
      } else {
        result.push(expressionObject);
      }
    }

    if (Array.isArray(expressionObject)) {
      const updatedExpression = findDataRouteIdAndRemove({
        dataRouteId,
        originalExpression: expressionObject,
      });

      if (updatedExpression.length === 1) {
        result.push(updatedExpression[0]);
      } else if (
        updatedExpression.length > 1
        || (!updatedExpression.length && allowEmptyParentGroup)
      ) {
        result.push(updatedExpression as ExpressionTerm[]);
      }
    }

    return result;
  }, []);
}

function assignDefaultMLAComponents({
  mlaComponents,
}: {
  mlaComponents: AttributeComponent[];
}) {
  return mlaComponents.map(({ attributeComponent, value }) => {
    let updatedDefaultValue;
    if (!value && attributeComponent.default && !attributeComponent.isOptional) {
      if (attributeComponent.dataType === 'csv') {
        updatedDefaultValue = attributeComponent.default;
      } else if (attributeComponent.dataType === 'multi_select') {
        updatedDefaultValue = [attributeComponent.default];
      } else {
        updatedDefaultValue = attributeComponent.default;
      }
    } else {
      updatedDefaultValue = undefined;
    }

    return {
      value: updatedDefaultValue,
      attributeComponent,
    };
  });
}

export function assignDefaultsToDataRoute({
  path,
  dataRoute: oldDataRoute,
  workflowData,
  workflowProps,
  value,
}: {
  path: string;
  dataRoute: DataRoute;
  workflowData: SchemaInstance;
  workflowProps: WorkflowProps;
  value: string | number;
}) {
  // !path means we're creating the route
  const suppliedAttribute = !path && oldDataRoute?.attribute?.id != null
    ? { ...oldDataRoute.attribute }
    : null;

  // Create a new object with values nulled out
  const dataRoute = {
    ...oldDataRoute,
    category: oldDataRoute?.category ?? {
      id: null,
      name: null,
    },
    attribute: suppliedAttribute ?? {
      id: null,
      token: null,
      displayName: '',
      attributeName: '',
      multiLevelAttributeFilter: {
        filterComponents: [],
      },
    },
  };
  const { services, universalAttributes } = workflowProps;
  let sourceAction: Action = null;
  let attribute: Attribute = null;

  if (!path || path === 'sourceId') {
    if (!path) {
      sourceAction = workflowData.actions[dataRoute.sourceId];
    } else {
      dataRoute.sourceId = value as string;
      sourceAction = workflowData.actions[value];
    }

    // Try to determine where the Attribute is coming from and what the data type of it is
    if (sourceAction.type === ActionType.SERVICE) {
      // Attribute belongs to a Service, track it down in the services object.
      const { attributes } = services[sourceAction.service.internalName];
      if (dataRoute.attribute.id === null) {
        // A service can have no eligible attribute at all (json-only, or all archived) -
        // leave the route unattributed rather than reading off an empty list.
        const defaultAttribute = getDefaultAttribute({ action: sourceAction, workflowProps });
        dataRoute.attribute.id = defaultAttribute?.attribute_id ?? null;
        dataRoute.attribute.token = defaultAttribute?.attribute_token ?? null;
      }

      attribute = resolveServiceOrUniversalAttribute(
        attributes,
        universalAttributes,
        dataRoute.attribute.id,
      );
      dataRoute.attribute.displayName = attribute?.display_name;
      dataRoute.attribute.attributeName = attribute?.attribute_name;
      if (attribute?.is_multilevel) {
        const mlaComponents = transformMLAAttributeComponents({
          attributeFilterComponents: attribute.components,
        });
        const remappedMLAComponents = assignDefaultMLAComponents({ mlaComponents });
        dataRoute.attribute.multiLevelAttributeFilter = {
          filterComponents: remappedMLAComponents,
        };
      } else {
        dataRoute.attribute.multiLevelAttributeFilter = null;
      }

      if (isAggregationServiceAction(sourceAction) && attribute) {
        dataRoute.aggregationComponents = resolveAggregationComponents(
          attribute,
          workflowProps.aggregationConfig,
          !path ? dataRoute.aggregationComponents : undefined,
        );
      } else {
        dataRoute.aggregationComponents = [];
      }
    }
  } else if (path === 'attribute.id') {
    /* The attribute is being updated, set the requisite values
     */
    sourceAction = workflowData.actions[dataRoute.sourceId];
    const { attributes: serviceAttributes } = services[sourceAction.service.internalName];
    attribute = resolveServiceOrUniversalAttribute(
      serviceAttributes,
      universalAttributes,
      value as number,
    );
    dataRoute.attribute.id = Number(value);
    dataRoute.attribute.token = attribute?.attribute_token;
    dataRoute.attribute.displayName = attribute?.display_name;
    dataRoute.attribute.attributeName = attribute?.attribute_name;

    if (attribute?.is_multilevel) {
      const mlaComponents = transformMLAAttributeComponents({
        attributeFilterComponents: attribute.components,
      });
      const remappedMLAComponents = assignDefaultMLAComponents({ mlaComponents });
      dataRoute.attribute.multiLevelAttributeFilter = {
        filterComponents: remappedMLAComponents,
      };
    } else {
      dataRoute.attribute.multiLevelAttributeFilter = null;
    }

    if (isAggregationServiceAction(sourceAction) && attribute) {
      dataRoute.aggregationComponents = resolveAggregationComponents(
        attribute,
        workflowProps.aggregationConfig,
      );
    } else {
      dataRoute.aggregationComponents = [];
    }
  }

  return dataRoute;
}

export function addNewItemToExpression({
  dataRouteId,
  type,
  pathToPerformActionAt,
  originalExpression,
}: {
  dataRouteId?: string;
  type: ExpressionUpdateType;
  pathToPerformActionAt: string;
  originalExpression: Expression | [];
}) {
  const expressionData = cloneDeep(originalExpression);

  // new item is either data route id object, or a new empty group.
  const newItem = dataRouteId
    ? {
      data_route_id: dataRouteId,
    }
    : [] as ExpressionTerm[];

  if (expressionData.length === 0) {
    // no items in the expression at all
    return [newItem];
  }

  // if path has `[` string, basically it means parth consists of array indices that point to a specific location in the expression array.
  // and that means we either append or insert a new data route id to that position. path would be like this: [0][1] / [1]
  if (pathToPerformActionAt?.includes('[')) {
    const indices = getNumericPathIndices(pathToPerformActionAt);
    const {
      pathWithoutLastIndex,
      parentExpressionArrayOfTheExpressionOnTheLastIndex,
    } = getParentPathAndExpressionItem({
      indices,
      existingExpression: expressionData,
    });

    if (type === ExpressionUpdateType.ADDITION) {
      if (indices.length === 1) {
        const updatedRule = setProperty(
          expressionData,
          `${Number(indices[0]) + 1}`,
          newItem,
        );

        return updatedRule;
      }

      parentExpressionArrayOfTheExpressionOnTheLastIndex[Number(indices.at(-1)) + 1] = newItem;
      setProperty(
        expressionData,
        pathWithoutLastIndex,
        parentExpressionArrayOfTheExpressionOnTheLastIndex,
      );

      return expressionData;
    }

    if (type === ExpressionUpdateType.INSERTION) {
      if (indices.length === 1) {
        const expressionItemOnPath: Expression = getProperty(expressionData, pathToPerformActionAt);
        if (Array.isArray(expressionItemOnPath) && !expressionItemOnPath.length) {
          // oh this is group and if path is simply [number], it means this group is new and empty
          (expressionData[Number(indices[0])] as ExpressionTerm[]).push(newItem as ExpressionTerm); // insert new one in the array
        } else {
          expressionData.splice(Number(indices[0]) + 1, 0, newItem);
        }
      } else {
        parentExpressionArrayOfTheExpressionOnTheLastIndex.splice(
          Number(indices.at(-1)) + 1, 0, newItem);
        setProperty(
          expressionData,
          pathWithoutLastIndex,
          parentExpressionArrayOfTheExpressionOnTheLastIndex,
        );
      }

      return expressionData;
    }
  } else if (expressionData.length > 0 && !pathToPerformActionAt) {
    // in this case, path is '', but there is an expression already; meaning user tried adding a new item at the very beginning.
    return [newItem, ...expressionData];
  } else {
    // this means path was not provided or path doesn't have `[` string in it.
    // so we are just gonna append a new one at the end of the expression array.
    return [
      ...expressionData,
      newItem,
    ];
  }

  return expressionData;
}

export function removeDataRouteFromExpression({
  dataRouteId,
  originalExpression,
  allowEmptyParentGroup,
}: {
  dataRouteId: string;
  originalExpression: Expression | [];
  allowEmptyParentGroup?: boolean;
}) {
  const expressionData = cloneDeep(originalExpression);

  return findDataRouteIdAndRemove({
    dataRouteId,
    originalExpression: expressionData,
    allowEmptyParentGroup,
  });
}

export function modifyRelevantDataFromDataRouteRemoval({
  targetDataRoute,
  dataRouteId,
  workflowData,
  allowEmptyParentGroup,
}: {
  targetDataRoute: DataRoute;
  dataRouteId: string;
  workflowData: Partial<SchemaInstance>;
  allowEmptyParentGroup?: boolean;
}) {
  const targetAction = workflowData.actions[targetDataRoute.targetId];
  if (targetAction.type === ActionType.OUTPUT) {
    const updatedExpression = removeDataRouteFromExpression({
      dataRouteId,
      originalExpression: targetAction.output.expression,
      allowEmptyParentGroup,
    });

    workflowData.actions[targetDataRoute.targetId].output.expression = updatedExpression;
  }
}
